import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';

const CallsheetAnalytics = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [selectedUserProjects, setSelectedUserProjects] = useState([]);
  const [isProjectsDialogOpen, setIsProjectsDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Fetch users
    const { data: usersData, error: usersError } = await supabase
      .from('user')
      .select('*');

    if (usersError) {
      console.error('Error fetching users:', usersError);
      return;
    }

    // Fetch projects
    const { data: projectsData, error: projectsError } = await supabase
      .from('project')
      .select('*');

    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
      return;
    }

    // Fetch callsheets
    const { data: callsheetsData, error: callsheetsError } = await supabase
      .from('callsheet')
      .select('*');

    if (callsheetsError) {
      console.error('Error fetching callsheets:', callsheetsError);
      return;
    }

    // Process data to combine users with their projects and callsheets
    const usersWithCallsheets = usersData.map(user => {
      const userProjects = projectsData.filter(project => project.user_id === user.id);
      const projectCallsheets = userProjects.map(project => {
        const callsheets = callsheetsData.filter(
          callsheet => callsheet.project_id === project.project_id
        );
        return {
          ...project,
          callsheetCount: callsheets.length,
          created_at: project.created_at
        };
      });

      const totalCallsheets = projectCallsheets.reduce(
        (sum, project) => sum + project.callsheetCount, 
        0
      );

      return {
        ...user,
        totalCallsheets,
        projects: projectCallsheets
      };
    });

    setUsers(usersWithCallsheets);
    setFilteredUsers(usersWithCallsheets);
    processGraphData(usersWithCallsheets);
  };

  const processGraphData = (userData) => {
    // Get top 10 users by callsheet count for the graph
    const topUsers = [...userData]
      .sort((a, b) => b.totalCallsheets - a.totalCallsheets)
      .slice(0, 10);

    const graphData = topUsers.map(user => ({
      name: user.name || 'Unknown',
      callsheets: user.totalCallsheets
    }));

    setGraphData(graphData);
  };

  const handleSearch = () => {
    let filtered = users;

    if (searchName) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchMobile) {
      filtered = filtered.filter(user =>
        user.phone?.includes(searchMobile)
      );
    }

    setFilteredUsers(filtered);
  };

  const handleCallsheetsClick = (projects) => {
    setSelectedUserProjects(projects);
    setIsProjectsDialogOpen(true);
  };

  return (
    <div className="callsheet-analytics">
      <h2>Callsheet Analytics</h2>

      {/* Bar Chart */}
      <div style={{ height: '400px', marginBottom: '2rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="callsheets" fill="#ffa726" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <TextField
          label="Search by Mobile"
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Total No. of Callsheets</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleCallsheetsClick(user.projects)}
                    variant="text"
                    color="primary"
                  >
                    {user.totalCallsheets}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Projects and Callsheets Dialog */}
      <Dialog
        open={isProjectsDialogOpen}
        onClose={() => setIsProjectsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Project-wise Callsheets</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S. No.</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Created On</TableCell>
                  <TableCell>No. of Callsheets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedUserProjects.map((project, index) => (
                  <TableRow key={project.project_id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{project.project_name}</TableCell>
                    <TableCell>
                      {format(new Date(project.created_at), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>{project.callsheetCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallsheetAnalytics; 